// 跑馬燈 — 對照 Unity SlotFramework BottomBarUI 的 Marquee（BottomBar.prefab 內、贏分顯示區）：
//   閒置（沒有贏分要顯示）時於中央輪播宣傳橫幅，由右往左捲動、播完換下一則；
//   一有贏分即隱藏、改由 BottomBarView 顯示贏分（互斥，對應 BottomBarUIMediator.OnSetWin
//   的 ShowMarquee(!isTargetWin, hasWinValue)）。
// 訊息本體照 Unity 為 4 張多語系字圖 tx_score_1~4（S026_AssetTable per-locale；掛於
//   Marquee/NormalMask 的 Image~Image(3)，LandscapeUI/PoriraitUI 覆寫 KeyId 綁定；
//   遮罩 550×100、圖寬 1188/1668 恆寬於遮罩故必捲動）；字圖未載入時退回多語系文字。
// 節奏照 Unity BottomBarUI.PlayMarquee：寬於視窗＝DOLocalMoveX Ease.Linear 固定
//   _marqueeDuration=5s 由右捲到左、前置 _marqueeDelay=1s（本實作以則間 GAP 呈現）；
//   窄於視窗＝原地置中停留 5s；播完換下一則無限輪播。

import { Container, Graphics, Sprite, Text, type Texture } from 'pixi.js'
import { fontFamilyForLocale } from '../../../framework/i18n/i18n'
import { killTweensOf, tweenTo } from '../tween'

/** 單則捲動時長（秒，Unity BottomBarUI._marqueeDuration=5：不論寬度固定 5 秒捲完）。 */
const SCROLL_DURATION_SEC = 5
/** 訊息若可完整放入視窗，原地置中停留秒數（Unity 窄訊息停留 _marqueeDuration 後換下一則）。 */
const STATIC_HOLD_SEC = 5
/** 每則訊息之間的間隔秒數（對應 Unity _marqueeDelay=1，Unity 為捲動 tween 的前置 delay）。 */
const GAP_SEC = 1
/** 橫幅顯示高佔視窗高的比例（Unity 圖高 80 / 遮罩高 100）。 */
const BANNER_HEIGHT_RATIO = 0.8

export class MarqueeView extends Container {
  private readonly maskShape = new Graphics()
  /** 字圖橫幅（tx_score_1~4；空陣列＝文字模式）。 */
  private readonly bannerSprites: Sprite[] = []
  // 命名避開 Pixi Container 內建的 `label` 屬性。文字為字圖未載入時的 fallback。
  private readonly messageText = new Text({
    text: '',
    style: { fontFamily: fontFamilyForLocale(), fontSize: 22, fill: 0xffe45c, stroke: { color: 0x2b0f38, width: 4 } },
  })
  // 停留用的可中斷計時對象（透過 killTweensOf 一併取消）。
  private readonly holdProxy = { t: 0 }
  private viewW = 0
  private index = 0
  private running = false

  constructor(private readonly messages: readonly string[], banners: readonly Texture[] = []) {
    super()
    this.messageText.anchor.set(0, 0.5) // 左錨，x 即左緣，便於由右往左捲動與置中計算
    this.maskShape.eventMode = 'none'
    this.addChild(this.maskShape, this.messageText)
    for (const texture of banners) {
      const sprite = new Sprite(texture)
      sprite.anchor.set(0, 0.5)
      sprite.visible = false
      this.bannerSprites.push(sprite)
      this.addChild(sprite)
    }
    this.mask = this.maskShape // 將訊息裁切在視窗內（對應 Unity NormalMask 的 RectMask2D）
    this.visible = false
  }

  /** 輪播則數（字圖模式＝橫幅張數、文字模式＝訊息數）。 */
  private get itemCount(): number {
    return this.bannerSprites.length > 0 ? this.bannerSprites.length : this.messages.length
  }

  /** 設定可視視窗尺寸與字級（中心在本容器原點 (0,0)）；本容器位置由外層 resize 設定。 */
  setViewport(width: number, height: number, fontSize: number): void {
    this.viewW = width
    this.messageText.style.fontSize = fontSize
    // 橫幅依視窗高等比縮放（Unity 圖高 80／遮罩高 100 的比例）。
    for (const sprite of this.bannerSprites) {
      const tex = sprite.texture
      if (tex.height > 0) {
        const h = height * BANNER_HEIGHT_RATIO
        sprite.height = h
        sprite.width = (tex.width / tex.height) * h
      }
    }
    this.maskShape.clear().rect(-width / 2, -height / 2, width, height).fill({ color: 0xffffff })
  }

  /** 開始循環播放（idempotent）；未設視窗尺寸前會先短暫等待，尺寸就緒後自動開始捲動。 */
  start(): void {
    if (this.running || this.itemCount === 0) return
    this.running = true
    this.visible = true
    void this.loop()
  }

  /** 停止並隱藏（與贏分顯示互斥時由 BottomBarView 呼叫）。 */
  stop(): void {
    this.running = false
    killTweensOf([this.messageText, ...this.bannerSprites, this.holdProxy])
    this.visible = false
  }

  private async loop(): Promise<void> {
    while (this.running) {
      await this.playOne()
      if (!this.running) break
      this.index = (this.index + 1) % this.itemCount
      await this.hold(GAP_SEC)
    }
  }

  /** 播放目前索引的訊息一次（窄則置中停留、寬則由右捲到左；固定 5 秒捲完）。 */
  private async playOne(): Promise<void> {
    if (this.viewW <= 0) {
      // 尺寸尚未就緒（建構後、首次 resize 前）：短暫等待再回圈重試。
      await this.hold(0.3)
      return
    }
    // 選定本則顯示物件：字圖模式顯示對應橫幅、其餘隱藏；文字模式沿用單一 Text。
    const banner = this.bannerSprites[this.index] ?? null
    for (const sprite of this.bannerSprites) sprite.visible = sprite === banner
    this.messageText.visible = banner === null
    if (banner === null) {
      this.messageText.text = this.messages[this.index] ?? ''
    }
    const item = banner ?? this.messageText
    const itemW = item.width

    if (itemW <= this.viewW) {
      item.x = -itemW / 2 // 置中
      await this.hold(STATIC_HOLD_SEC)
      return
    }

    // 由完全在右側外（左緣在右邊界）捲到完全在左側外（右緣在左邊界），固定時長（Unity _marqueeDuration）。
    const startX = this.viewW / 2
    const endX = -this.viewW / 2 - itemW
    item.x = startX
    await tweenTo(item, { x: endX, duration: SCROLL_DURATION_SEC, ease: 'none' })
  }

  /** 可中斷的停留（stop() 會 killTweensOf(holdProxy) 使其立即 resolve）。 */
  private hold(sec: number): Promise<void> {
    this.holdProxy.t = 0
    return tweenTo(this.holdProxy, { t: 1, duration: sec })
  }

  override destroy(options?: Parameters<Container['destroy']>[0]): void {
    this.stop()
    super.destroy(options)
  }
}
